import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Search, X, Eye, Edit, Trash2, RefreshCcw, CheckCircle, DollarSign, Calculator, Users, Shield, ChevronDown, ChevronUp, Download, Upload, FileSpreadsheet, FileOutput, List, LayoutList, LayoutGrid, Layers, Filter, Link, Clock, XCircle } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getJournalComptableEntries,
  createJournalComptableEntry,
  updateJournalComptableEntry,
  deleteJournalComptableEntry,
  getChartOfAccounts,
} from '../../shared/utils/comptabiliteService';
import { buildChartAccountOptions, CHART_ACCOUNTS_REFRESH_EVENT } from './utils/chartAccountOptions';
import {
  loadAnalyticalSelectOptions,
  ANALYTICAL_CODES_REFRESH_EVENT,
} from './utils/comptabiliteAnalyticalOptions';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

const JOURNAL_LIST_COLUMNS = [
  { key: 'journal_ref', labelKey: 'col_reference' },
  { key: 'date_ecriture', labelKey: 'col_entry_date' },
  { key: 'libelle', labelKey: 'col_label' },
  { key: 'comptes', labelKey: 'col_accounts' },
  { key: 'montant', labelKey: 'col_amount' },
  { key: 'statut_ecriture', labelKey: 'col_status' },
];

const JournalComptablePage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  useComptabiliteConfig();
  const p = (key, opts) => t(`modules.comptabilite.pages.journalComptablePage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);

  const getStatusLabel = (status) => {
    const map = {
      Validé: p('status_validated'),
      Brouillon: p('status_draft'),
      Clôturé: p('status_closed'),
      Annulé: p('status_cancelled') };
    return map[status] || status || p('na');
  };

  const journalTypeOptions = [
    { value: 'Achats', labelKey: 'type_purchases', isDefault: true },
    { value: 'Ventes', labelKey: 'type_sales', isDefault: true },
    { value: 'Banque', labelKey: 'type_bank', isDefault: true },
    { value: 'Caisse', labelKey: 'type_cash', isDefault: true },
    { value: 'OD', labelKey: 'type_od', isDefault: true },
    { value: 'AN', labelKey: 'type_an', isDefault: true },
    { value: 'Paie', labelKey: 'type_payroll', isDefault: true },
    { value: 'Trésorerie', labelKey: 'type_treasury', isDefault: true },
  ];

  const tiersTypeOptions = [
    { value: 'Fournisseur', labelKey: 'tiers_supplier', isDefault: true },
    { value: 'Client', labelKey: 'tiers_client', isDefault: true },
    { value: 'Autre', labelKey: 'tiers_other', isDefault: true },
  ];

  const statusOptions = [
    { value: 'Brouillon', labelKey: 'status_draft', isDefault: true },
    { value: 'Validé', labelKey: 'status_validated', isDefault: true },
    { value: 'Clôturé', labelKey: 'status_closed', isDefault: true },
    { value: 'Annulé', labelKey: 'status_cancelled', isDefault: true },
  ];

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'Brouillon', labelKey: 'status_draft', icon: Clock, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Validé', labelKey: 'status_validated', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Clôturé', labelKey: 'status_closed', icon: FileText, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'Annulé', labelKey: 'status_cancelled', icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], p), [t]);

  const getTypeLabel = (type) => {
    const map = Object.fromEntries(
      journalTypeOptions.map(({ value, labelKey }) => [value, p(labelKey)])
    );
    return map[type] || type || p('na');
  };

  // ===== STATE MANAGEMENT =====
  const [journals, setJournals] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [analyticalPosteOptions, setAnalyticalPosteOptions] = useState([]);
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [viewingJournal, setViewingJournal] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Advanced Options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_journals');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Date range filter
  const [setDateRange] = useState({ start: null, end: null });
  
  // Dropdowns
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Selection & Sorting
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  
  // Expanded sections for GPS-style collapsible form (4 sections)
  const [journalComptableFormTab, setJournalComptableFormTab] = useState('journalInfo');
  const journalComptableFormTabs = useMemo(() => ([
    { id: 'journalInfo', label: p('section_journal_info'), icon: ChevronUp },
    { id: 'comptesInfo', label: p('section_accounts_amounts'), icon: ChevronUp },
    { id: 'analytiqueValidation', label: p('section_validation_controls'), icon: ChevronUp }
  ]), [p]);

  
  // FormData - ALL FIELDS INITIALIZED WITH ''
  const [formData, setFormData] = useState({
    journal_ref: '',
    code_journal: '',
    libelle_journal: '',
    type_journal: '',
    date_journal: '',
    periode_comptable: '',
    exercice_fiscal: '',
    numero_ecriture: '',
    date_ecriture: '',
    date_piece: '',
    libelle_ecriture: '',
    libelle_piece: '',
    numero_piece: '',
    reference_externe: '',
    compte_debit: '',
    compte_credit: '',
    montant: '',
    tiers_code: '',
    tiers_nom: '',
    tiers_type: '',
    analytique_affectation: '',
    analytique_montant: '',
    tva_code: '',
    tva_taux: '',
    tva_montant: '',
    tva_deductible: '',
    mode_paiement: '',
    moyen_paiement_numero: '',
    echeance_date: '',
    echeance_statut: '',
    lettrage_code: '',
    lettrage_automatique: '',
    validation_ecriture: '',
    validation_date: '',
    validateur: '',
    statut_ecriture: '',
    ecriture_simulation: '',
    ecriture_extourne: '',
    commentaires_saisie: '',
    notes_validation: '',
    notes: ''
  });

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadJournals();
    loadChartAccounts();
    loadAnalyticalOptions();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);

  const loadAnalyticalOptions = async () => {
    try {
      const opts = await loadAnalyticalSelectOptions();
      setAnalyticalPosteOptions(opts.postes);
    } catch (error) {
      console.error('Error loading analytical codes:', error);
      setAnalyticalPosteOptions([]);
    }
  };

  const loadChartAccounts = async () => {
    try {
      const rows = await getChartOfAccounts();
      setChartAccounts(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading chart accounts:', error);
      setChartAccounts([]);
    }
  };

  useEffect(() => {
    const onChartRefresh = () => { void loadChartAccounts(); };
    const onAnalyticalRefresh = () => { void loadAnalyticalOptions(); };
    window.addEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onChartRefresh);
    window.addEventListener(ANALYTICAL_CODES_REFRESH_EVENT, onAnalyticalRefresh);
    return () => {
      window.removeEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onChartRefresh);
      window.removeEventListener(ANALYTICAL_CODES_REFRESH_EVENT, onAnalyticalRefresh);
    };
  }, []);

  const accountOptions = useMemo(
    () => buildChartAccountOptions(chartAccounts),
    [chartAccounts],
  );

  const handleAccountPick = (field, numero) => {
    setFormData((prev) => ({ ...prev, [field]: numero }));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const columnFilterRules = useMemo(() => [
    textColumnRule('journal_ref', (row) => row.journal_ref ?? ''),
    textColumnRule('date_ecriture', (row) => row.date_ecriture ?? ''),
    textColumnRule('montant', (row) => String(row.montant ?? '')),
    textColumnRule('statut_ecriture', (row) => row.statut_ecriture ?? ''),
  ], []);

  // Select handlers
  
  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => deleteJournalComptableEntry(id)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label_plural', { defaultValue: 'قيود اليومية' }));
        setSelectedRecords([]);
        clearSelection();
        loadJournals();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { defaultValue: 'تعذّر حذف القيود المحددة' }));
      }
    }
  };

  const loadJournals = async () => {
    try {
      setLoading(true);
      const response = await getJournalComptableEntries();
      setJournals(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading journals:', error);
      setJournals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { 
        ...formData,
        montant: parseFloat(formData.montant) || 0,
        tva_taux: parseFloat(formData.tva_taux) || 0,
        tva_montant: parseFloat(formData.tva_montant) || 0,
        analytique_montant: parseFloat(formData.analytique_montant) || 0,
        validation_ecriture: formData.validation_ecriture ? 1 : 0,
        lettrage_automatique: formData.lettrage_automatique ? 1 : 0,
        ecriture_simulation: formData.ecriture_simulation ? 1 : 0,
        ecriture_extourne: formData.ecriture_extourne ? 1 : 0,
        tva_deductible: formData.tva_deductible ? 1 : 0
      };
      
      if (selectedJournal) {
        await updateJournalComptableEntry(selectedJournal.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'قيد اليومية' }));
      } else {
        await createJournalComptableEntry(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'قيد اليومية' }));
      }
      await loadJournals();
      resetForm();
    } catch (error) {
      console.error('Error saving journal entry:', error);
      notifyError(showNotify, p('alert_save_error', { defaultValue: 'تعذّر حفظ القيد' }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (journal) => {
    setSelectedJournal(journal);
    setFormData({
      ...journal,
      date_journal: journal.date_journal ? journal.date_journal.split('T')[0] : '',
      date_ecriture: journal.date_ecriture ? journal.date_ecriture.split('T')[0] : '',
      date_piece: journal.date_piece ? journal.date_piece.split('T')[0] : '',
      echeance_date: journal.echeance_date ? journal.echeance_date.split('T')[0] : '',
      validation_date: journal.validation_date ? journal.validation_date.split('T')[0] : ''
    });
    setJournalComptableFormTab('journalInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await deleteJournalComptableEntry(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'قيد اليومية' }));
        await loadJournals();
      } catch (error) {
        console.error('Error deleting journal entry:', error);
        notifyError(showNotify, p('alert_save_error', { defaultValue: 'تعذّر حذف القيد' }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (journal) => {
    setViewingJournal(journal);
    setShowViewModal(true);
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const journal = journals.find((j) => j.id === recordId);
      if (!journal) return;
      await updateJournalComptableEntry(recordId, { ...journal, statut_ecriture: newStatus });
      await loadJournals();
    } catch (error) {
      console.error('Error updating journal status:', error);
    }
  };

  const defaultExerciceFiscal = () => fiscalYearLabel || resolveFiscalYearLabel();

  const openNewJournalModal = () => {
    setSelectedJournal(null);
    setFormData({
      journal_ref: '', code_journal: '', libelle_journal: '', type_journal: '', date_journal: '',
      periode_comptable: '', exercice_fiscal: defaultExerciceFiscal(), numero_ecriture: '', date_ecriture: '', date_piece: '',
      libelle_ecriture: '', libelle_piece: '', numero_piece: '', reference_externe: '',
      compte_debit: '', compte_credit: '', montant: '', tiers_code: '', tiers_nom: '', tiers_type: '',
      analytique_affectation: '', analytique_montant: '', tva_code: '', tva_taux: '', tva_montant: '',
      tva_deductible: '', mode_paiement: '', moyen_paiement_numero: '', echeance_date: '', echeance_statut: '',
      lettrage_code: '', lettrage_automatique: '', validation_ecriture: '', validation_date: '', validateur: '',
      statut_ecriture: '', ecriture_simulation: '', ecriture_extourne: '', commentaires_saisie: '',
      notes_validation: '', notes: '',
    });
    setJournalComptableFormTab('journalInfo');
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      journal_ref: '', code_journal: '', libelle_journal: '', type_journal: '', date_journal: '',
      periode_comptable: '', exercice_fiscal: defaultExerciceFiscal(), numero_ecriture: '', date_ecriture: '', date_piece: '',
      libelle_ecriture: '', libelle_piece: '', numero_piece: '', reference_externe: '',
      compte_debit: '', compte_credit: '', montant: '', tiers_code: '', tiers_nom: '', tiers_type: '',
      analytique_affectation: '', analytique_montant: '', tva_code: '', tva_taux: '', tva_montant: '',
      tva_deductible: '', mode_paiement: '', moyen_paiement_numero: '', echeance_date: '', echeance_statut: '',
      lettrage_code: '', lettrage_automatique: '', validation_ecriture: '', validation_date: '', validateur: '',
      statut_ecriture: '', ecriture_simulation: '', ecriture_extourne: '', commentaires_saisie: '',
      notes_validation: '', notes: ''
    });
    setSelectedJournal(null);
    setShowModal(false);
    setJournalComptableFormTab('journalInfo');
  };

  // Stats - 5 STATS CARDS
  const stats = {
    total: journals.length,
    validees: journals.filter(j => j.statut_ecriture === 'Validé').length,
    brouillon: journals.filter(j => j.statut_ecriture === 'Brouillon').length,
    montantTotal: journals.reduce((sum, j) => sum + (parseFloat(j.montant) || 0), 0),
    aLettrer: journals.filter(j => !j.lettrage_code).length
  };

  // Distribution data
  const journalDistribution = {
    all_journals: {
      titleKey: 'dist_overview',
      data: [
        { filterKey: 'validated', nameKey: 'dist_validated', count: stats.validees, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'draft', nameKey: 'dist_draft', count: stats.brouillon, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'to_letter', nameKey: 'dist_to_letter', count: stats.aLettrer, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    by_type: {
      titleKey: 'dist_by_type',
      data: [
        { filterKey: 'Achats', nameKey: 'dist_purchases', count: journals.filter(j => j.type_journal === 'Achats').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'Ventes', nameKey: 'dist_sales', count: journals.filter(j => j.type_journal === 'Ventes').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'Banque', nameKey: 'dist_bank', count: journals.filter(j => j.type_journal === 'Banque').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'Caisse', nameKey: 'dist_cash', count: journals.filter(j => j.type_journal === 'Caisse').length, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    by_status: {
      titleKey: 'dist_by_status',
      data: [
        { filterKey: 'Validé', nameKey: 'dist_status_validated', count: stats.validees, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'Brouillon', nameKey: 'dist_status_draft', count: stats.brouillon, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'Clôturé', nameKey: 'dist_status_closed', count: journals.filter(j => j.statut_ecriture === 'Clôturé').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'Annulé', nameKey: 'dist_status_cancelled', count: journals.filter(j => j.statut_ecriture === 'Annulé').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  };

  const distributionInfo = journalDistribution[mainViewMode] || journalDistribution.all_journals;

  // Main view tabs
  const mainTabs = [
    { id: 'all_journals', labelKey: 'tab_overview', icon: LayoutGrid },
    { id: 'by_type', labelKey: 'tab_by_type', icon: FileText },
    { id: 'by_status', labelKey: 'tab_by_status', icon: Shield }
  ];

  // Quick filter buttons
  const quickFilterButtons = [
    { id: 'all', labelKey: 'filter_all', icon: Layers },
    { id: 'Brouillon', labelKey: 'filter_draft', icon: Edit },
    { id: 'Validé', labelKey: 'filter_validated', icon: CheckCircle },
    { id: 'to_letter', labelKey: 'filter_to_letter', icon: Link }
  ];

  const statCards = [
    { labelKey: 'stat_total', value: stats.total, icon: FileText, color: 'from-amber-500 to-amber-700' },
    { labelKey: 'stat_validated', value: stats.validees, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_draft', value: stats.brouillon, icon: Edit, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_total_amount', value: formatBrandMoney(stats.montantTotal), icon: DollarSign, color: 'from-emerald-500 to-emerald-600' },
    { labelKey: 'stat_to_letter', value: stats.aLettrer, icon: Link, color: 'from-cyan-500 to-cyan-600' }
  ];

  const filteredJournals = useMemo(() => {
    const base = journals.filter(journal => {

      let matchesFilter = true;
      if (quickFilter === 'to_letter') {
        matchesFilter = !journal.lettrage_code;
      } else if (quickFilter !== 'all') {
        matchesFilter = journal.statut_ecriture === quickFilter;
      }

      if (activeDistributionFilter) {
        if (mainViewMode === 'by_status') {
          if (journal.statut_ecriture !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'by_type') {
          if (journal.type_journal !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'all_journals') {
          if (activeDistributionFilter === 'validated' && journal.statut_ecriture !== 'Validé') return false;
          if (activeDistributionFilter === 'draft' && journal.statut_ecriture !== 'Brouillon') return false;
          if (activeDistributionFilter === 'to_letter' && journal.lettrage_code) return false;
        }
      }

      if (accountFilter) {
        const acct = accountFilter.trim();
        if (journal.compte_debit !== acct && journal.compte_credit !== acct) return false;
      }

      const matchesSearch = !searchTerm ||
        journal.journal_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        journal.code_journal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        journal.numero_piece?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        journal.libelle_ecriture?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [journals, quickFilter, activeDistributionFilter, mainViewMode, searchTerm, columnFilters, columnFilterRules, accountFilter]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredJournals, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(JOURNAL_LIST_COLUMNS, p);
  const journalTableColSpan = visibleDataColumnCount + 2;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="h-8 w-8 text-amber-400" />
            {p('title')}
            <button onClick={loadJournals} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={openNewJournalModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_entry')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - 5 STATS with GPS Glow Effect */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options Section */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            {/* Main View Mode Tabs */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setMainViewMode(tab.id); setActiveDistributionFilter(null); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {p(tab.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Filters */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {p(btn.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-w-md">
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">
                {p('filter_by_account', { defaultValue: 'تصفية حسب الحساب' })}
              </h3>
              <CustomSelect
                value={accountFilter}
                onChange={setAccountFilter}
                options={[{ value: '', label: p('filter_all', { defaultValue: 'الكل' }) }, ...accountOptions]}
                placeholder={p('placeholder_compte_debit', { defaultValue: 'اختر حساباً' })}
                searchable
                autoSelect={false}
              />
            </div>

            {/* Distribution Overview */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white/80 text-sm font-semibold uppercase tracking-wider">{p(distributionInfo.titleKey)}</h3>
                {activeDistributionFilter && (
                  <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                    <XCircle size={14} />{p('reset_filter')}
                  </button>
                )}
              </div>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                  <button key={item.nameKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                      <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                      <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                        const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                        return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      })()}%` }}></div>
                    </div>
                    <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of', { percent: (() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })() })}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('journal_ref') ? (
                    <TableColumnHeaderFilter columnKey="journal_ref" label={p('col_reference')} value={columnFilters.journal_ref || ''} onChange={(v) => setColumnFilter('journal_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('date_ecriture') ? (
                    <TableColumnHeaderFilter columnKey="date_ecriture" label={p('col_entry_date')} value={columnFilters.date_ecriture || ''} onChange={(v) => setColumnFilter('date_ecriture', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('libelle') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_label')}</th>
                  ) : null}
                  {isColVisible('comptes') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_accounts')}</th>
                  ) : null}
                  {isColVisible('montant') ? (
                    <TableColumnHeaderFilter columnKey="montant" label={p('col_amount')} value={columnFilters.montant || ''} onChange={(v) => setColumnFilter('montant', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_ecriture') ? (
                    <TableColumnHeaderFilter columnKey="statut_ecriture" label={p('col_status')} value={columnFilters.statut_ecriture || ''} onChange={(v) => setColumnFilter('statut_ecriture', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredJournals.length > 0 ? (
                  filteredJournals.map((journal, index) => (
                    <tr 
                      key={journal.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(journal.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(journal.id)}
                              onChange={() => handleSelectRecord(journal.id)}
                            />
                          </div>
                        </td>
                      {isColVisible('journal_ref') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{journal.journal_ref || p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{journal.code_journal || '-'}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('date_ecriture') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {journal.date_ecriture ? new Date(journal.date_ecriture).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : p('na')}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">{journal.numero_piece || p('no_document')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('libelle') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{journal.libelle_ecriture || p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{getTypeLabel(journal.type_journal)}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('comptes') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{p('debit_prefix')} {journal.compte_debit || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('credit_prefix')} {journal.compte_credit || '-'}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('montant') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{journal.montant ? formatBrandMoney(journal.montant) : p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('amount_label')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('statut_ecriture') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={journal.statut_ecriture || 'Brouillon'}
                            recordId={journal.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(journal)}
                          onEdit={() => handleEdit(journal)}
                          onDelete={() => handleDelete(journal.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={journalTableColSpan} className="text-center py-12 text-white/60">
                      <FileText size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_subtitle')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredJournals.length > 0 ? (
              filteredJournals.map((journal, index) => (
                <motion.div
                  key={journal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <FileText size={20} className="text-amber-400" />
                          {journal.journal_ref || p('na')}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={journal.statut_ecriture || 'Brouillon'}
                          recordId={journal.id}
                          options={statusBadgeOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_journal_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_code')}</span> {journal.code_journal || p('na')}</div>
                            <div><span className="font-medium">{p('card_type')}</span> {getTypeLabel(journal.type_journal)}</div>
                            <div><span className="font-medium">{p('card_piece_no')}</span> {journal.numero_piece || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_accounts_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_debit')}</span> {journal.compte_debit || '-'}</div>
                            <div><span className="font-medium">{p('card_credit')}</span> {journal.compte_credit || '-'}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium text-green-300">{p('card_amount')}</span> {journal.montant ? formatBrandMoney(journal.montant) : p('na')}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_tiers_analytic')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_tiers')}</span> {journal.tiers_nom || '-'}</div>
                            <div><span className="font-medium">{p('card_code')}</span> {journal.tiers_code || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_date_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_date')}</span> {journal.date_ecriture ? new Date(journal.date_ecriture).toLocaleDateString(dateLocale) : '-'}</div>
                            <div><span className="font-medium">{p('card_validated')}</span> {journal.validation_ecriture ? p('yes') : p('no')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(journal)}
                      onEdit={() => handleEdit(journal)}
                      onDelete={() => handleDelete(journal.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <FileText size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - GPS ReactDOM Portal with 4 Collapsible Sections */}
      {showModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
                <FileText className="h-6 w-6 text-amber-400" />
                {selectedJournal ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="journal-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedJournal ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {journalComptableFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = journalComptableFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setJournalComptableFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="journal-form" onSubmit={handleSubmit}>
                {journalComptableFormTab === 'journalInfo' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code_journal')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.code_journal}
                              onChange={(e) => setFormData({ ...formData, code_journal: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_code_journal')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_libelle_journal')}</label>
                            <input
                              type="text"
                              value={formData.libelle_journal}
                              onChange={(e) => setFormData({ ...formData, libelle_journal: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_libelle_journal')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type_journal')}</label>
                            <CustomSelect
                              value={formData.type_journal}
                              onChange={(val) => setFormData({ ...formData, type_journal: val })}
                              options={journalTypeOptions.map((opt) => ({ ...opt, label: p(opt.labelKey) }))}
                              placeholder={p('placeholder_select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date_journal')}</label>
                            <CustomDatePicker
                              value={formData.date_journal}
                              onChange={(dateString) => setFormData({ ...formData, date_journal: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date_ecriture')}</label>
                            <CustomDatePicker
                              value={formData.date_ecriture}
                              onChange={(dateString) => setFormData({ ...formData, date_ecriture: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_periode_comptable')}</label>
                            <input
                              type="text"
                              value={formData.periode_comptable}
                              onChange={(e) => setFormData({ ...formData, periode_comptable: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_periode')}
                            />
                          </div>
                          <ComptabiliteFiscalYearField
                            label={p('label_exercice_fiscal', { defaultValue: 'السنة المالية' })}
                            value={formData.exercice_fiscal}
                            onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })}
                            placeholder={fiscalYearLabel || resolveFiscalYearLabel()}
                            required
                          />
                          <div className="">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_libelle_ecriture')}</label>
                            <input
                              type="text"
                              value={formData.libelle_ecriture}
                              onChange={(e) => setFormData({ ...formData, libelle_ecriture: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_libelle_ecriture')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_numero_piece')}</label>
                            <input
                              type="text"
                              value={formData.numero_piece}
                              onChange={(e) => setFormData({ ...formData, numero_piece: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_numero_piece')}
                            />
                          </div>
                  </div>
                </div>
                )}
                {journalComptableFormTab === 'comptesInfo' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_compte_debit')}</label>
                            <CustomSelect
                              value={formData.compte_debit}
                              onChange={(val) => handleAccountPick('compte_debit', val)}
                              options={accountOptions}
                              placeholder={p('placeholder_compte_debit')}
                              searchable
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_compte_credit')}</label>
                            <CustomSelect
                              value={formData.compte_credit}
                              onChange={(val) => handleAccountPick('compte_credit', val)}
                              options={accountOptions}
                              placeholder={p('placeholder_compte_credit')}
                              searchable
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_montant')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.montant}
                              onChange={(v) => setFormData({ ...formData, montant: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tva_code')}</label>
                            <input
                              type="text"
                              value={formData.tva_code}
                              onChange={(e) => setFormData({ ...formData, tva_code: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_tva_code')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tva_taux')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.tva_taux}
                              onChange={(v) => setFormData({ ...formData, tva_taux: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tva_montant')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.tva_montant}
                              onChange={(v) => setFormData({ ...formData, tva_montant: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
</div>
                )}
                {journalComptableFormTab === 'analytiqueValidation' && (
                  <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_analytique_affectation', { defaultValue: 'التحليل (موقع/مركز)' })}</label>
                            <CustomSelect
                              value={formData.analytique_affectation}
                              onChange={(val) => setFormData({ ...formData, analytique_affectation: val })}
                              options={analyticalPosteOptions}
                              placeholder={p('placeholder_analytique', { defaultValue: 'اختر رمزاً تحليلياً' })}
                              searchable
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_analytique_montant', { defaultValue: 'مبلغ التحليل (ر.س)' })}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.analytique_montant}
                              onChange={(v) => setFormData({ ...formData, analytique_montant: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tiers_code')}</label>
                            <input
                              type="text"
                              value={formData.tiers_code}
                              onChange={(e) => setFormData({ ...formData, tiers_code: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_tiers_code')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tiers_nom')}</label>
                            <input
                              type="text"
                              value={formData.tiers_nom}
                              onChange={(e) => setFormData({ ...formData, tiers_nom: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_tiers_nom')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_tiers_type')}</label>
                            <CustomSelect
                              value={formData.tiers_type}
                              onChange={(val) => setFormData({ ...formData, tiers_type: val })}
                              options={tiersTypeOptions.map((opt) => ({ ...opt, label: p(opt.labelKey) }))}
                              placeholder={p('placeholder_select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_lettrage_code')}</label>
                            <input
                              type="text"
                              value={formData.lettrage_code}
                              onChange={(e) => setFormData({ ...formData, lettrage_code: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_lettrage')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_echeance_date')}</label>
                            <CustomDatePicker
                              value={formData.echeance_date}
                              onChange={(dateString) => setFormData({ ...formData, echeance_date: dateString })}
                              placeholder={p('placeholder_select_date')}
                            />
                          </div>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_statut_ecriture')}</label>
                            <CustomSelect
                              value={formData.statut_ecriture}
                              onChange={(val) => setFormData({ ...formData, statut_ecriture: val })}
                              options={statusOptions.map((opt) => ({ ...opt, label: p(opt.labelKey) }))}
                              placeholder={p('placeholder_select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_validateur')}</label>
                            <input
                              type="text"
                              value={formData.validateur}
                              onChange={(e) => setFormData({ ...formData, validateur: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_validateur')}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_notes')}
                            />
                          </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal - GPS Pattern with 4 Categorized Sections */}
      {showViewModal && viewingJournal ? ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => { setShowViewModal(false); setViewingJournal(null); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FileText className="h-6 w-6 text-amber-400" />
                {p('modal_view_title')}
              </h2>
              <button onClick={() => { setShowViewModal(false); setViewingJournal(null); }} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Section 1: Informations Journal */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <FileText size={18} className="text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_journal_info')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_reference')}</p>
                    <p className="text-white font-semibold">{viewingJournal.journal_ref || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_code_journal')}</p>
                    <p className="text-white font-semibold">{viewingJournal.code_journal || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_type')}</p>
                    <p className="text-white font-semibold">{getTypeLabel(viewingJournal.type_journal)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_entry_date')}</p>
                    <div className="flex items-center gap-1.5 text-white font-semibold">
                      <Clock size={14} className="text-white/50" />
                      {viewingJournal.date_ecriture ? new Date(viewingJournal.date_ecriture).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : p('na')}
                    </div>
                  </div>
                  <div className="md:col-span-4">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label')}</p>
                    <p className="text-white font-semibold">{viewingJournal.libelle_ecriture || p('na')}</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Comptes & Montants */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Calculator size={18} className="text-amber-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_accounts_amounts')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_debit_account')}</p>
                    <p className="text-white font-semibold">{viewingJournal.compte_debit || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_credit_account')}</p>
                    <p className="text-white font-semibold">{viewingJournal.compte_credit || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_amount')}</p>
                    <p className="text-white font-bold text-xl">{viewingJournal.montant ? formatBrandMoney(viewingJournal.montant) : p('na')}</p>
                  </div>
                </div>
              </div>

              {/* Grid for Sections 3 & 4 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Section 3: Tiers & Analytique */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Users size={18} className="text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_tiers_analytic')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_tiers')}</p>
                      <p className="text-white font-semibold">{viewingJournal.tiers_nom || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_tiers_code')}</p>
                      <p className="text-white font-semibold">{viewingJournal.tiers_code || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_lettrage_code')}</p>
                      <p className="text-white font-semibold">{viewingJournal.lettrage_code || p('na')}</p>
                    </div>
                  </div>
                </div>

                {/* Section 4: Validation & Contrôles */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Shield size={18} className="text-violet-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_validation')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_status')}</p>
                      <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium border ${
                        viewingJournal.statut_ecriture === 'Validé' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        viewingJournal.statut_ecriture === 'Brouillon' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                        'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }`}>
                        {getStatusLabel(viewingJournal.statut_ecriture || 'Brouillon')}
                      </span>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_validated')}</p>
                      <p className="text-white font-semibold">{viewingJournal.validation_ecriture ? p('yes') : p('no')}</p>
                    </div>
                    {viewingJournal.validateur && (
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_validateur')}</p>
                        <p className="text-white font-semibold">{viewingJournal.validateur}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {viewingJournal.notes && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <p className="text-white/60 text-xs uppercase tracking-wider mb-2">{p('label_notes')}</p>
                  <p className="text-white/70 text-sm">{viewingJournal.notes}</p>
                </div>
              )}
            </div>

            <button
              onClick={() => { setShowViewModal(false); setViewingJournal(null); }}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {p('close')}
            </button>
          </motion.div>
        </div>,
        document.body
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default JournalComptablePage;

